import React, { useState, useEffect, useMemo } from 'react';
import contactApi from '../../services/contactApi.js';
import { EmptyState, LoadingState } from '../Common/PanelState';
import { toast } from 'react-toastify';

const FLARE_RED = '#c4151c';
const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const STATUS_STYLES = {
  new: { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200', label: 'New' },
  read: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200', label: 'Read' },
  replied: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', label: 'Replied' },
  resolved: { bg: 'bg-gray-100', text: 'text-gray-700', ring: 'ring-gray-300', label: 'Resolved' },
  closed: { bg: 'bg-gray-50', text: 'text-gray-500', ring: 'ring-gray-200', label: 'Closed' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-PK', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ContactRequestsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const list = await contactApi.listRequests();
      setRows(list);
    } catch (e) {
      toast.error(e?.message || 'Failed to load contact requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const all = rows || [];
    return {
      total: all.length,
      new: all.filter(r => r.status === 'new' || !r.status).length,
      read: all.filter(r => r.read).length,
      replied: all.filter(r => r.status === 'replied').length,
    };
  }, [rows]);

  const reasons = useMemo(() => {
    const s = new Set(['all']);
    rows.forEach(r => r.reason && s.add(r.reason));
    return Array.from(s);
  }, [rows]);

  const sources = useMemo(() => {
    const s = new Set(['all']);
    rows.forEach(r => r.source && s.add(r.source));
    return Array.from(s);
  }, [rows]);

  const SOURCE_STYLES = {
    'Website Contact': { bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-200' },
    'Flare Customer': { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200' },
    'website_contact_form': { bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-200' },
  };
  const sourceStyle = (src) => {
    if (!src) return { bg: 'bg-gray-50', text: 'text-gray-600', ring: 'ring-gray-200' };
    return SOURCE_STYLES[src] || { bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-200' };
  };

  const filtered = useMemo(() => {
    const q = (query || '').toString().trim().toLowerCase();
    return (rows || []).filter(r => {
      if (statusFilter !== 'all' && (r.status || 'new') !== statusFilter) return false;
      if (reasonFilter !== 'all' && (r.reason || '') !== reasonFilter) return false;
      if (sourceFilter !== 'all' && (r.source || '') !== sourceFilter) return false;
      if (!q) return true;
      return [r.name, r.email, r.phone, r.subject, r.message, r.id, r.reason, r.source]
        .map(v => (v || '').toString().toLowerCase())
        .some(v => v.includes(q));
    });
  }, [rows, query, statusFilter, reasonFilter, sourceFilter]);

  const openView = async (r) => {
    setView(r);
    setReply(r.reply || '');
    if (!r.read) {
      const updated = await contactApi.updateStatus(r.id, { read: true });
      setRows(updated);
    }
  };

  const updateStatus = async (r, status) => {
    const updated = await contactApi.updateStatus(r.id, { status });
    setRows(updated);
    setView(prev => prev?.id === r.id ? (updated.find(x => x.id === r.id) || prev) : prev);
    toast.success(`Status → ${STATUS_STYLES[status]?.label || status}`);
  };

  const toggleRead = async (r) => {
    const updated = await contactApi.updateStatus(r.id, { read: !r.read });
    setRows(updated);
    setView(prev => prev?.id === r.id ? (updated.find(x => x.id === r.id) || prev) : prev);
  };

  const submitReply = async () => {
    if (!view) return;
    const trimmed = (reply || '').toString().trim();
    if (trimmed.length < 2) {
      toast.warn('Reply should be at least 2 characters.');
      return;
    }
    setSaving(true);
    try {
      const updated = await contactApi.updateStatus(view.id, {
        reply: trimmed,
        status: 'replied',
      });
      setRows(updated);
      const fresh = updated.find(x => x.id === view.id) || view;
      setView(fresh);
      toast.success('✅ Reply saved & status updated to Replied');
    } catch (e) {
      toast.error(e?.message || 'Could not save reply.');
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete request from "${r.name || 'Unknown'}"? This cannot be undone.`)) return;
    const updated = await contactApi.deleteRequest(r.id);
    setRows(updated);
    if (view?.id === r.id) setView(null);
    toast.success('Request deleted.');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="admin-page-title flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow" style={{ backgroundColor: FLARE_RED }}>
              <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </div>
            Contact Requests
          </h1>
          <p className="text-sm text-gray-500 font-semibold mt-1">Messages from the <span className="font-black text-gray-800">/contact</span> page appear here instantly.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow text-gray-800 font-bold text-sm hover:bg-gray-50 transition-colors"
          >
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
          <a
            href="/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-bold text-sm shadow hover:shadow-lg transition-shadow"
            style={{ backgroundColor: FLARE_RED }}
          >
            <Icon path="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" className="h-4 w-4" />
            Open Contact Page
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total', value: stats.total, icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', color: 'from-gray-700 to-gray-900' },
          { label: 'New', value: stats.new, icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', color: 'from-red-500 to-red-700' },
          { label: 'Read', value: stats.read, icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z', color: 'from-amber-500 to-orange-600' },
          { label: 'Replied', value: stats.replied, icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', color: 'from-emerald-500 to-green-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${s.color} shadow-md`}>
                <Icon path={s.icon} className="h-5 w-5 text-white" />
              </div>
              <div className="text-3xl font-black text-gray-900">{s.value}</div>
            </div>
            <div className="text-xs font-black uppercase tracking-widest text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Icon path="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" className="h-4 w-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, subject, message, or ID…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none transition-colors font-medium text-sm placeholder:text-gray-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none font-bold text-sm text-gray-700 appearance-none bg-white cursor-pointer"
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_STYLES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none font-bold text-sm text-gray-700 appearance-none bg-white cursor-pointer min-w-[160px]"
          >
            {reasons.map(r => (
              <option key={r} value={r}>{r === 'all' ? 'All Reasons' : r}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none font-bold text-sm text-gray-700 appearance-none bg-white cursor-pointer min-w-[160px]"
          >
            {sources.map(r => (
              <option key={r} value={r}>{r === 'all' ? 'All Sources' : r}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <LoadingState label="Loading messages" />
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No messages yet"
              hint={query || statusFilter !== 'all' || reasonFilter !== 'all' ? 'Try removing filters or search terms.' : 'Messages submitted via the contact page will appear here instantly.'}
            />
          ) : (
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {filtered.map((r) => {
                const isOpen = view?.id === r.id;
                const status = r.status || 'new';
                const st = STATUS_STYLES[status] || STATUS_STYLES.new;
                return (
                  <div
                    key={r.id}
                    onClick={() => openView(r)}
                    className={`p-4 sm:p-5 cursor-pointer transition-colors ${isOpen ? 'bg-rose-50/60' : 'hover:bg-gray-50'} ${!r.read ? 'bg-white' : ''}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm font-black text-white text-sm ${r.read ? 'bg-gray-400' : ''}`} style={!r.read ? { backgroundColor: FLARE_RED } : {}}>
                        {(r.name || '?').trim().charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <div className={`font-black text-gray-900 truncate ${!r.read ? '' : 'font-semibold text-gray-700'}`}>
                            {r.name || 'Unknown person'}
                          </div>
                          {!r.read && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white shadow" style={{ backgroundColor: FLARE_RED }}>
                              New
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 ${st.bg} ${st.text} ${st.ring}`}>
                            {st.label}
                          </span>
                          {r.reason && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                              {r.reason}
                            </span>
                          )}
                          {r.source && (() => {
                            const st = sourceStyle(r.source);
                            return (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 ${st.bg} ${st.text} ${st.ring}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${st.text.replace('text-', 'bg-')}`} />
                                {r.source}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-xs sm:text-sm font-bold text-gray-800 mb-1 truncate">{r.subject}</div>
                        <div className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed font-medium">{r.message}</div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold text-gray-400">
                          <span className="inline-flex items-center gap-1"><Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-3.5 w-3.5" />{r.email || '—'}</span>
                          {r.phone && <span className="inline-flex items-center gap-1"><Icon path="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" className="h-3.5 w-3.5" />{r.phone}</span>}
                          <span className="inline-flex items-center gap-1"><Icon path="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5" />{formatDate(r.created_at)}</span>
                          <span className="inline-flex items-center gap-1 font-mono text-[10px]">{r.id}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleRead(r); }}
                          className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 shadow-sm flex items-center justify-center"
                          title={r.read ? 'Mark unread' : 'Mark read'}
                        >
                          <Icon path={r.read ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'} className="h-4 w-4 text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); del(r); }}
                          className="w-8 h-8 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 shadow-sm flex items-center justify-center"
                          title="Delete"
                        >
                          <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="xl:col-span-2">
          {!view ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center mx-auto mb-4">
                <Icon path="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" className="h-9 w-9 text-gray-400" />
              </div>
              <div className="font-black text-gray-900 text-xl mb-2">Select a message</div>
              <div className="text-sm text-gray-500 font-medium max-w-xs mx-auto">
                Click any message on the left to see full details, save a reply, or update status.
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3 bg-gradient-to-r from-rose-50/50 to-white">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1.5">Message ID <span className="font-mono text-gray-800">{view.id}</span></div>
                  <h2 className="font-black text-gray-900 text-lg mb-1 truncate">{view.subject}</h2>
                  <div className="text-sm font-bold text-gray-600 flex items-center gap-2 flex-wrap">
                    <span>{view.name || '—'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <a href={`mailto:${view.email}`} className="hover:underline" style={{ color: FLARE_RED }}>{view.email || '—'}</a>
                    {view.phone && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <a href={`tel:${view.phone}`} className="hover:underline text-gray-700">{view.phone}</a>
                      </>
                    )}
                  </div>
                </div>
                <button onClick={() => setView(null)} className="w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm flex items-center justify-center flex-shrink-0">
                  <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4 text-gray-600" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-0.5">Status</div>
                    <select
                      value={view.status || 'new'}
                      onChange={(e) => updateStatus(view, e.target.value)}
                      className="w-full bg-transparent outline-none font-black text-sm"
                    >
                      {Object.entries(STATUS_STYLES).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-0.5">Reason</div>
                    <div className="font-black text-sm text-gray-800 truncate">{view.reason || '—'}</div>
                  </div>
                  <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-0.5">Source</div>
                    <div className="font-black text-sm truncate">
                      {view.source ? (() => {
                        const st = sourceStyle(view.source);
                        return (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 ${st.bg} ${st.text} ${st.ring}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.text.replace('text-', 'bg-')}`} />
                            {view.source}
                          </span>
                        );
                      })() : <span className="text-gray-500">—</span>}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-0.5">Read</div>
                    <button onClick={() => toggleRead(view)} className={`font-black text-sm ${view.read ? 'text-emerald-700' : 'text-red-600'}`}>
                      {view.read ? '✓ Read (tap to unread)' : '✕ Unread (tap to read)'}
                    </button>
                  </div>
                  <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60 col-span-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-0.5">Created</div>
                    <div className="font-black text-sm text-gray-800 truncate">{formatDate(view.created_at)}</div>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">Message</div>
                  <div className="rounded-2xl border border-gray-100 p-5 bg-white text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-medium shadow-sm">
                    {view.message || <span className="italic text-gray-400">No message body.</span>}
                  </div>
                </div>

                {view.reply && (
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Previous Reply Saved
                    </div>
                    <div className="rounded-2xl border-2 border-emerald-200 p-5 bg-emerald-50 text-sm text-emerald-900 leading-relaxed whitespace-pre-wrap font-medium">
                      {view.reply}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">Your Reply / Notes</div>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={5}
                    placeholder="Type the reply or internal note you want to save. Mark the message as Replied, then send the actual reply via email to the customer."
                    className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-red-400 outline-none transition-colors font-medium text-sm text-gray-900 placeholder:text-gray-400 leading-relaxed resize-y min-h-[120px]"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
                  <button
                    onClick={() => del(view)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 text-red-700 font-black text-sm transition-colors"
                  >
                    <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" className="h-4 w-4" />
                    Delete Message
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateStatus(view, 'read')}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 text-gray-800 font-black text-sm transition-colors"
                    >
                      Mark Read
                    </button>
                    <button
                      onClick={submitReply}
                      disabled={saving}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-black text-sm shadow-lg hover:shadow-xl transition-shadow disabled:opacity-60"
                      style={{ backgroundColor: FLARE_RED, boxShadow: '0 10px 25px -12px rgba(196,21,28,0.55)' }}
                    >
                      {saving ? (
                        <>
                          <span className="w-4 h-4 rounded-full border-[3px] border-white/30" style={{ borderTopColor: '#fff', animation: 'flare-spin 0.8s linear infinite' }} />
                          Saving…
                        </>
                      ) : (
                        <>
                          <Icon path="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" className="h-4 w-4" />
                          Save Reply → Replied
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes flare-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
